import cx from 'classnames';
import React, { PropsWithChildren, useEffect, useState } from 'react';

type Props = PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title?: string;
  footer?: React.ReactNode;
  mobileViewOnly?: boolean;
}>;

export const Modal: React.FC<Props> = ({
  open,
  onClose,
  title,
  children,
  footer,
  mobileViewOnly,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!mounted) {
      return;
    }
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [mounted]);

  if (!mounted) {
    return null;
  }

  return (
    <div
      className={cx(
        'tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-end tw-justify-center',
        !mobileViewOnly && 'sm:tw-items-center',
        'tw-transition-colors tw-duration-300',
        visible ? 'tw-bg-black/70' : 'tw-bg-transparent',
      )}
      onClick={onClose}
    >
      <div
        className={cx(
          'tw-w-full tw-bg-component tw-flex tw-flex-col tw-max-w-[480px] tw-max-h-[90vh]',
          'tw-transition-all tw-duration-300',
          !mobileViewOnly
            ? 'tw-rounded-t-2xl sm:tw-rounded-2xl'
            : 'tw-rounded-t-2xl',
          !mobileViewOnly
            ? visible
              ? 'tw-translate-y-0 sm:tw-translate-y-0 sm:tw-opacity-100 sm:tw-scale-100'
              : 'tw-translate-y-full sm:tw-translate-y-0 sm:tw-opacity-0 sm:tw-scale-95'
            : visible
            ? 'tw-translate-y-0'
            : 'tw-translate-y-full',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-pt-6 tw-pb-4">
          {title && (
            <h3 className="tw-text-lg tw-font-semibold tw-text-primary tw-mr-4">
              {title}
            </h3>
          )}
          <button
            type="button"
            className="tw-ml-auto tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-text-primary tw-shrink-0"
            onClick={onClose}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M1 1l10 10M11 1L1 11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="tw-px-6 tw-pb-4 tw-overflow-y-auto tw-flex-1">
          {children}
        </div>

        {footer && <div className="tw-px-6 tw-pt-2 tw-pb-6">{footer}</div>}
      </div>
    </div>
  );
};
